import React, { useEffect, useMemo, useState } from "react";
import SharedFunctions from "../../util/SharedFunctions";
import moment from "moment";
import { Title } from "../../components/Title";
import { Container } from "../../components/Container";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { version } from "../../../package.json";
import UserUtils from "../../util/UserUtils";
import { useAuth } from "../../hooks/useAuthWithPermissions";
import ApiClient from "../../api/client";
import APIUtils from "../../api/APIUtils";

const Notification = ({ message, alertColour }) => (
  <div
    className={`dashboard-notification dashboard-notification-${alertColour.toLowerCase()} ln-c-alert ln-c-alert--fluid ln-c-alert--error`}
    role="alert"
  >
    <div className="ln-c-alert__wrapper">
      <div className="ln-c-alert__icon-wrapper">
        {alertColour.toLowerCase() === "red" && (
          <svg
            aria-label="Error"
            role="img"
            className="ln-c-alert__icon ln-c-icon"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Error circle</title>
            <path
              d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm1.376-14.168c0 .048-.016.096-.016.144l-.896 5.808h-.928l-.896-5.808c0-.048-.016-.096-.016-.144 0-.72.72-1.152 1.376-1.152.656 0 1.376.432 1.376 1.152zm-.112 8.048c0 .704-.56 1.264-1.264 1.264s-1.264-.56-1.264-1.264.56-1.264 1.264-1.264 1.264.56 1.264 1.264z"
              fill="currentColor"
              fillRule="evenodd"
            ></path>
          </svg>)}
      </div>
      <div className="ln-c-alert__content">
        {message}
      </div>
    </div>
  </div>
);

const ACTIVITY_DAY_OPTIONS = [
  { value: 7, label: "Last 7 Days" },
  { value: 14, label: "Last 14 Days" },
  { value: 30, label: "Last 30 Days" },
  { value: 90, label: "Last 90 Days" },
];


function HomePage({ loggedIn }) {
  const apiClient = new ApiClient(process.env.REACT_APP_AWS_URL);
  const { instance, accounts } = useMsal();
  const [errorNotifications, setErrorNotifications] = useState();
  const [errorActivity, setErrorActivity] = useState();
  const [notifications, setNotifications] = useState([]);
  const [processingActivity, setProcessingActivity] = useState([]);
  const [activityDays, setActivityDays] = useState(7);

  const { refetch } = useAuth();



  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await apiClient.get(`/${APIUtils.GATE_API}/DashboardAlerts`, {
          headers: await APIUtils.getAuthorizationHeader(),
        });

        if (response.success && response.data.items.length > 0) {
          setNotifications(response.data.items);
        }
      } catch (err) {
        setErrorNotifications("Failed to fetch notifications.");
        console.error("Error fetching notifications:", err);
      }
    };

    const fetchProcessingActivity = async () => {
      try {
        const response = await apiClient.get(`/${APIUtils.GATE_API}/ActivityLog`, {
          headers: await APIUtils.getAuthorizationHeader(),
        });

        if (response.success && response.data.items.length > 0) {
          setProcessingActivity(response.data.items);
        }
      } catch (err) {
        setErrorActivity("Failed to fetch processing activity.");
        console.error("Error fetching processing activity:", err);
      }
    };

    if(loggedIn) {
      fetchNotifications();
      fetchProcessingActivity();
    }
  }, []);

  //reload User data in local storage if it doesn't exist
  const hasUserData = UserUtils.hasUserData();
  useEffect(() => {
    if (loggedIn && !hasUserData) {
      refetch();
    }
  }, [loggedIn, hasUserData, refetch]);

  // Fallback: If MSAL templates don't work, use direct check
  if (!loggedIn) {
    const loginRequest = {
      scopes: [`api://${process.env.REACT_APP_SSO_CLIENTID}/access_as_user`]
    };

    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-sm-12 login">
            <div className="text-center">
              <button
                className="ln-c-button ln-c-button--filled"
                onClick={() => instance.loginRedirect(loginRequest)}
              >
                Login
              </button>
            </div>

            <div className="row">
              <div className="col-md-12 text-center" style={{ paddingTop: 60 }}>
                Version {version}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filteredActivity = useMemo(() => {
    const cutoff = moment().subtract(activityDays, 'days').startOf('day');
    return processingActivity
      .filter(a => a.logShowOnDashboard && moment(a.logDateTime).isSameOrAfter(cutoff))
      .sort((a, b) => moment(b.logDateTime).diff(moment(a.logDateTime)));
  }, [processingActivity, activityDays]);

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <div className="container-fluid">
          <div className="row row-eq-height">
            <div className="col-sm-12 content">
              <Title
                title={`${SharedFunctions.getGreetingTime(moment())}, ${accounts.length > 0 && accounts[0].name
                  }`}
              />

              <Container>
                <h4 className="luna-h4">Notifications</h4>
                {errorNotifications && <Notification message={errorNotifications} isError={true} alertColour="red" />}
                {!errorNotifications && notifications.length === 0 && <Notification message="No notifications today!" isError={false} alertColour="green" />}
                {!errorNotifications && notifications.length > 0 && notifications.map((notification) => (
                  <Notification key={notification.dashAlertId} message={notification.dashAlertText} isError={false} alertColour={notification.dashAlertColour} />
                ))}

                <h4 className="luna-h4" style={{ marginTop: 40 }}>Activity</h4>
                <div style={{ marginBottom: 16 }}>
                  <select
                    value={activityDays}
                    onChange={(e) => setActivityDays(Number(e.target.value))}
                    className="ln-c-select"
                  >
                    {ACTIVITY_DAY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                {errorActivity && <Notification message={errorActivity} isError={true} alertColour="red" />}
                {!errorActivity && filteredActivity.length === 0 && <Notification message="No activity found for the selected period." isError={false} alertColour="green" />}
                {!errorActivity && filteredActivity.length > 0 && (
                  <table>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Message</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredActivity.map((activity) => (
                        <tr key={activity.logId}>
                          <td className={`activity-log log-type-${activity.logColour.toLowerCase()}`}>{activity.logType.toUpperCase()}</td>
                          <td>{activity.logText}</td>
                          <td>{moment(activity.logDateTime).format("YYYY-MM-DD HH:mm:ss")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Container>
            </div>
          </div>
        </div>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-sm-12 login">
              <div className="text-center">
                <button
                  className="ln-c-button ln-c-button--filled"
                  onClick={() => instance.loginPopup()}
                >
                  Login
                </button>
              </div>

              <div className="row">
                <div
                  className="col-md-12 text-center"
                  style={{ paddingTop: 60 }}
                >
                  Version {version}
                </div>
              </div>
            </div>
          </div>
        </div>
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
}

export default HomePage;
